<script lang="ts">
import { Image } from '@builder.io/sdk-svelte';
import { getLinkAttributes, stripSiteDomain } from '$lib/utils/url.js';

interface Company {
    image: Record<string, unknown>;
    link?: string;
    description?: string;
}

interface Props {
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    companies?: Company[];
    hideBlock?: boolean;
}

let {
    title = '',
    headingLevel = 'h2',
    titleSize = 'h2',
    companies = [],
    hideBlock = false,
}: Props = $props();

const titleSizeClassName = $derived(`title--${titleSize}`);

// Transform URLs in companies array from Builder.io
const transformedCompanies = $derived(
    companies.map((company) => {
        const link = stripSiteDomain(company.link);

        return {
            ...company,
            link,
            linkAttributes: getLinkAttributes(link, { target: '_blank' }),
        };
    }),
);
</script>

{#if !hideBlock}
    <div class="partners">
        <div class="intro">
            {#if title}
                <svelte:element this={headingLevel} class="title {titleSizeClassName}">{title}</svelte:element>
            {/if}
        </div>

        {#each transformedCompanies as company, index (index)}
            <a
                class="company"
                data-aos="fade-up"
                href={company.link || null}
                {...company.linkAttributes}
            >
                <Image {...(company.image as any)} className="logo" />
                <span class="description">{company.description}</span>
            </a>
        {/each}
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use '/src/scss/global-constants/fonts';
    @use 'sass-mq/mq' as *;

    .partners {
        display: grid;
        gap: 2rem;
        grid-template-columns: repeat(2, 1fr);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        @include mq($from: tablet) {
            grid-template-columns: repeat(3, 1fr);
            column-gap: 10rem;
            row-gap: 4rem;
        }
    }

    .company {
        display: block;
    }

    .intro {
        @include mq($until: tablet) {
            grid-column: 1 / -1;
        }
    }

    .title {
        color: var(--colour-dreamless-sleep);
        margin-bottom: 1.6rem;
        max-width: 12ch;
        text-wrap: balance;

        @include mq($from: tablet) {
            margin-bottom: 2.4rem;
        }

        &--h1   { @include typography.display-xl; }
        &--h1-5 { @include typography.display-hero; }
        &--h2   { @include typography.display-lg; }
        &--h3   { @include typography.display-md; }
        &--h4   { @include typography.heading-lg; }
        &--h5   { @include typography.heading-md; }
    }

    .description {
        color: var(--colour-dreamless-sleep);
        margin-bottom: 1.2rem;
        @include typography.body-base;

        @include mq($from: tablet) {
            margin-bottom: 0;
        }
    }

    .partners :global(.logo) {
        margin-bottom: 1.6rem;
        height: 2.5rem;

        @include mq($from: tablet) {
            margin-bottom: 2.4rem;
            height: 4.5rem;
        }
    }

    .description {
        color: var(--colour-squant);
        display: block;
        @include typography.label-sm;
    }
</style>
